import { use, useMemo } from 'react';
import { css as gooberCss } from 'goober';
import merge from 'lodash/merge';

import type { Component, Element, IConfig, LayoutChild, TemplateType } from '~/config/types';
import type { DistributiveOmit } from '~/types/distributive-omit';
import { ConfigurationContext } from '~/components/providers/config-provider/context';
import { isElement, isTemplateReference } from '~/config/utils';
import { cn } from '~/utils/cn';

export function useConfig() {
  const context = use(ConfigurationContext);

  if (!context) {
    throw new Error('useConfig must be used within a ConfigurationProvider');
  }

  return context;
}

export function useLayout() {
  const config = useConfig();

  return useMemo(() => {
    return config.layout.map(child => {
      return configureChild(child, config);
    });
  }, [config]);
}

export function useTemplate(templateName: TemplateType | (string & {})) {
  const config = useConfig();

  return useMemo(() => {
    const template = config.templates[templateName];

    if (!template) {
      throw new Error(`Template for component type "${templateName}" not found.`);
    }

    return configureChild(template, config) as ConfiguredElement;
  }, [templateName, config]);
}

// Exclude template reference from children
type ConfiguredElement = DistributiveOmit<Element, 'children'> & {
  children?: (ConfiguredChild | Component)[] | string;
};
export type ConfiguredChild = ConfiguredElement | Component;

/**
 * Goes through the layout child and its children recursively,
 * basically excludes template references by resolving them to the actual template.
 *
 * If the child is an element, it only merges its CSS with the template's CSS.
 * If the child is a template reference, it resolves the template from the config.
 * If the child is a component, it does nothing with it, as components' templates are resolved in the render function.
 */
function configureChild(child: LayoutChild, config: IConfig) {
  if (isElement(child) || isTemplateReference(child)) {
    const template = child.template ? config.templates[child.template] : null;
    const css = merge({}, template?.css, child.css);
    const className = Array.from(
      new Set(
        cn(
          template?.elementProps.className,
          child.elementProps?.className,
          Object.keys(css).length && gooberCss(css)
        ).split(' ')
      )
    ).join(' ');

    // prefer child's children over template's children
    if (child.children) {
      child.children = configureChildren(child.children, config);
    } else if (template?.children) {
      child.children = configureChildren(template.children, config);
    }

    return {
      ...child,
      elementProps: { ...template?.elementProps, ...child.elementProps, className },
      css,
    } as ConfiguredChild;
  }

  if (child.children) {
    child.children = configureChildren(child.children, config);
  }

  return child;
}

function configureChildren(children: LayoutChild[] | string | undefined, config: IConfig) {
  return Array.isArray(children) ? children.map(child => configureChild(child, config)) : children;
}
